/**
 * LocationSearchInput Component
 * 
 * This component provides a Google Places Autocomplete input field for location search.
 * It handles the initialization of the Google Maps API and creates an autocomplete instance
 * that suggests locations as the user types.
 * 
 * Key features:
 * - Loads Google Maps API only once across multiple instances
 * - Restricts searches to Australia
 * - Returns formatted address and coordinates when a place is selected
 * - Handles loading states and errors
 * 
 * IMPORTANT IMPLEMENTATION NOTES:
 * 1. When a place is selected, it calls the onPlaceSelected callback with the formatted address
 *    and coordinates (lat/lng).
 * 2. The parent component (e.g., Search.tsx) is responsible for updating state and URL parameters
 *    based on the selected place.
 * 3. This component maintains its own input value state, which is updated when a place is selected.
 * 4. The defaultValue prop is used for initial rendering and will update the input when changed.
 * 5. The component can be clicked to focus and clear the input, making it easy to search again.
 */
import { useState, useEffect, useRef } from "react";
import { loadGoogleMapsApi } from "../../utils/googleMaps";

interface LocationSearchInputProps {
  onPlaceSelected: (place: { formattedAddress: string; coordinates: { lat: number; lng: number } }) => void;
  defaultValue?: string;
  placeholder?: string;
  className?: string;
}

// Keep a shared reference to avoid multiple instances
let mapsApiLoadInitiated = false;

export function LocationSearchInput({
  onPlaceSelected,
  defaultValue = '',
  placeholder = 'Search for a location...',
  className = '',
}: LocationSearchInputProps) {
  const [inputValue, setInputValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInputFocused, setIsInputFocused] = useState(false);

  // Initialize the autocomplete instance
  useEffect(() => {
    const initAutocomplete = async () => {
      const input = inputRef.current;
      if (!input) return;

      try {
        // Set the flag before loading to prevent parallel loading attempts
        if (!mapsApiLoadInitiated) {
          mapsApiLoadInitiated = true;
          // Load the Google Maps API using our centralized loader with minimal logging
          await loadGoogleMapsApi(false);
        } else {
          // If already initiated, just wait for it to complete
          await loadGoogleMapsApi(false);
        }

        // Create a new autocomplete instance
        const autocomplete = new google.maps.places.Autocomplete(input, {
          componentRestrictions: { country: "au" },
          fields: ["formatted_address", "geometry"]
        });

        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          
          if (place?.geometry?.location && place.formatted_address) {
            // Call the callback with the selected place data
            onPlaceSelected({
              formattedAddress: place.formatted_address,
              coordinates: {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng()
              }
            });
            
            // Update the input value to show the selected place
            setInputValue(place.formatted_address);
            // Blur the input to hide the dropdown
            input.blur();
          }
        });

        autocompleteRef.current = autocomplete;
      } catch (error) {
        console.error("Error initializing Google Maps:", error);
      } finally {
        setIsLoading(false);
      }
    };

    // Initialize the autocomplete when the component mounts
    initAutocomplete();

    // Cleanup function
    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [onPlaceSelected]);

  // Update input value when defaultValue changes
  useEffect(() => {
    if (defaultValue !== inputValue) {
      setInputValue(defaultValue);
    }
  }, [defaultValue]);

  // When input is focused, select all text to make it easy to replace
  const handleFocus = () => {
    if (inputRef.current) {
      inputRef.current.select();
    }
    setIsInputFocused(true);
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // Handle click on the container
  const handleContainerClick = () => {
    if (inputRef.current && !isInputFocused) {
      inputRef.current.focus();
    }
  };

  return (
    <div 
      className="relative cursor-text w-full"
      onClick={handleContainerClick}
    >
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={() => setIsInputFocused(false)}
        placeholder={placeholder}
        className={`${className} ${isLoading ? 'opacity-50' : ''}`}
        style={{ 
          width: '100%'
        }}
        autoComplete="off"
        disabled={isLoading}
      />
    </div>
  );
}